import { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'

import { isEmpty } from 'lodash'

import { TYPES_OF_TILE } from '../../../../../../constants'

import { getViewRecords, getViewTabViewRecords } from 'components/data/dataServices'
import { getUserVariables } from 'components/core/services/environmentService'
import { driverDrivesColumns } from '../../driver-drives/helper/constants'
import { mobileUnitTilePrimaryKeyColumns } from '../../mobile-unit/MobileUnitsForm'

export async function getDatagridField(tileSetting, apiToken) {
	const viewKey = tileSetting?.viewKey || ''
	const viewType = tileSetting?.viewType || ''

	const res = await getViewTabViewRecords({
		viewKey,
		token: apiToken,
		shouldRemoveViewSession: true,
		viewType,
	})

	return res.data.columns || []
}

export async function getChartField(tileSetting, apiToken) {
	const viewKey = tileSetting?.viewKey || ''

	const res = await getViewTabViewRecords({
		viewKey,
		token: apiToken,
		shouldRemoveViewSession: true,
	})

	return res.data.columns || []
}

export async function getMobileUnitField(apiToken) {
	const res = await getUserVariables(apiToken)

	const userVariables = Array.isArray(res.data) ? res.data : []

	const userVariableFields = userVariables.map((variable, index) => ({
		title: variable.title,
		name: `Variable${index + 1}`,
		mobileUnitKey: `variable${index + 1}`,
		hide: false,
	}))

	const combinedPrimaryKeyColumns = [...mobileUnitTilePrimaryKeyColumns, ...userVariableFields]

	return combinedPrimaryKeyColumns
}
export async function getDriverDrivesField() {
	return driverDrivesColumns
}

export async function getJobSummaryField(tileSetting, apiToken) {
	const formKey = tileSetting?.selectedJobForm?.formKey || ''
	const projectKey = tileSetting?.selectedJobForm?.projectKey || ''
	const res = await getViewRecords(formKey, projectKey, false, apiToken)
	return res.data.columns || []
}

export async function getIconSummaryField(tileSetting, apiToken) {
	const viewKey = tileSetting?.viewKey || ''
	const viewType = tileSetting?.viewType || ''

	const res = await getViewTabViewRecords({
		viewKey,
		token: apiToken,
		shouldRemoveViewSession: true,
		viewType,
	})

	return res.data.columns || []
}

const buildTileFieldQuerKeys = (tileKey, tileType, tileSetting) => {
	switch (tileType) {
		case TYPES_OF_TILE.DATA_GRID:
			return ['tile-field', tileKey, tileType, tileSetting?.viewKey, tileSetting?.viewType]
		default:
			return ['tile-field', tileKey, tileType]
	}
}

export function useTileFieldsQuery({ tileKey, tileType, tileSetting }) {
	const [tileFields, setTileFields] = useState([])
	const { environment } = useSelector((state) => state)

	const queryKey = useMemo(
		() => buildTileFieldQuerKeys(tileKey, tileType, tileSetting),
		[tileKey, tileType, tileSetting]
	)

	const { data, isLoading } = useQuery({
		queryKey,
		queryFn: async () => {
			switch (tileType) {
				case TYPES_OF_TILE.DATA_GRID:
					return await getDatagridField(tileSetting, environment?.apiToken)
				case TYPES_OF_TILE.CHART:
					return await getChartField(tileSetting, environment?.apiToken)
				case TYPES_OF_TILE.MOBILE_UNITS:
					return await getMobileUnitField(environment?.apiToken)
				case TYPES_OF_TILE.DRIVER_DRIVES:
					return await getDriverDrivesField()
				case TYPES_OF_TILE.JOB_SUMMARY:
					return await getJobSummaryField(tileSetting, environment?.apiToken)
				case TYPES_OF_TILE.ICON_SUMMARY:
					return await getIconSummaryField(tileSetting, environment?.apiToken)
				default:
					return []
			}
		},
		enabled: Boolean(tileKey) && Boolean(tileType) && Boolean(tileSetting),
		staleTime: Infinity,
	})
	useEffect(() => {
		if (isLoading) return

		if (isEmpty(data)) {
			setTileFields([])
			return
		}

		setTileFields(data || [])
	}, [data, isLoading])

	return {
		isLoadingFields: isLoading,
		tileFields,
	}
}
